import { useState, useEffect } from "react";
import { useApi } from "../../hooks/use-api";
import { useSingleton } from "../../hooks/use-singleton";
import {
  API_CREATE_ROOM,
  API_COURSES,
  apiCourseFilter,
} from "../../utils/api-routes";

const EXAM_CONFIG = {
  subject: {
    endpoint: "exam-subjects",
    dependsOn: ["board", "exam"],
  },

  chapter: {
    endpoint: "exam-chapters",
    dependsOn: ["board", "exam", "subject"],
  },
};

const CBSE_STATE_BOARD_CONFIG = {
  subject: {
    endpoint: "subjects",
    dependsOn: ["board", "clas", "medium"],
  },

  chapter: {
    endpoint: "chapters",
    dependsOn: ["board", "clas", "medium", "subject"],
  },
};

const HIERARCHY_API_CONFIG = {
  UPSC: {
    paper: {
      endpoint: "upsc/papers",
      dependsOn: ["board", "clas", "medium"],
    },

    subject: {
      endpoint: "upsc/subjects",
      dependsOn: ["board", "clas", "medium", "paper"],
    },

    unit: {
      endpoint: "upsc/units",
      dependsOn: ["board", "clas", "medium", "paper", "subject"],
    },

    lesson: {
      endpoint: "upsc/lessons",
      dependsOn: ["board", "clas", "medium", "paper", "subject", "unit"],
    },
  },
  TNPSC: {
    part: {
      endpoint: "tnpsc/parts",
      dependsOn: ["board", "clas", "medium"],
    },

    unit: {
      endpoint: "tnpsc/units",
      dependsOn: ["board", "clas", "medium", "part"],
    },

    chapter: {
      endpoint: "tnpsc/chapters",
      dependsOn: ["board", "clas", "medium", "part", "unit"],
    },

    lesson: {
      endpoint: "tnpsc/lessons",
      dependsOn: ["board", "clas", "medium", "part", "unit", "chapter"],
    },
  },

  CBSE: CBSE_STATE_BOARD_CONFIG,
  TN_STATE_BOARD: CBSE_STATE_BOARD_CONFIG,
  ICSE: CBSE_STATE_BOARD_CONFIG,
  CAIE: CBSE_STATE_BOARD_CONFIG,
  NEET: EXAM_CONFIG,
  JEE: EXAM_CONFIG,
};

export const useHandler = () => {
  const [course, setCourse] = useState([]);
  const [selectedValues, setSelectedValues] = useState({});
  const [selectedCourse, setSelectedCourse] = useState("");
  const [filterData, setFilterData] = useState({});
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [maxAttempts, setMaxAttempts] = useState(1);
  const [deadline, setDeadline] = useState("");
  const [mode, setMode] = useState("live");
  const [roomCode, setRoomCode] = useState("");
  console.log(roomCode, "roomcode");
  const { mail } = useSingleton();

  const { get, post } = useApi();

  useEffect(() => {
    getCourseDetails();
  }, []);

  const getCourseDetails = async () => {
    try {
      const res = await get(API_COURSES);
      if (res) {
        setCourse(res?.data?.course_types);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      console.log("Error on getting Course Details", e);
    }
  };

  const getCourseFilter = async (courseName) => {
    try {
      const res = await get(apiCourseFilter(courseName));
      console.log(res, "filtered response");
      setFilterData(res?.data);
    } catch (e) {
      console.log("Error on filtering course", e);
    }
  };

  const handleCourseChange = (e) => {
    const value = e.target.value;

    setSelectedCourse(value);

    setSelectedValues({});

    setDynamicOptions({});

    getCourseFilter(value);
  };

  const handleHierarchyChange = async (field, value) => {
    // store selected values
    const updatedValues = {
      ...selectedValues,
      [field]: value,
    };

    const clearedValues = { ...updatedValues };
    const currentIndex = filterData?.hierarchy?.indexOf(field);

    filterData?.hierarchy?.slice(currentIndex + 1)?.forEach((key) => {
      delete clearedValues[key];
    });

    setSelectedValues(clearedValues);
    // current hierarchy index

    // next hierarchy field
    const nextField = filterData?.hierarchy?.[currentIndex + 1];

    // stop if no next field
    if (!nextField) return;

    // course config
    const courseConfig = HIERARCHY_API_CONFIG?.[selectedCourse];
    // next field config
    const nextFieldConfig = courseConfig?.[nextField];

    // stop if no config
    if (!nextFieldConfig) return;

    try {
      // build payload dynamically
      const payload = {};

      nextFieldConfig.dependsOn.forEach((key) => {
        payload[key] = updatedValues[key];
      });

      console.log(payload, "dynamic payload");
      const updatedOptions = { ...dynamicOptions };

      filterData?.hierarchy?.slice(currentIndex + 1)?.forEach((key) => {
        delete updatedOptions[key];
      });

      setDynamicOptions(updatedOptions);

      // dynamic api hit
      const res = await post(nextFieldConfig.endpoint, payload);

      console.log(res, "next hierarchy response");

      // store options dynamically
      setDynamicOptions((prev) => ({
        ...prev,
        [nextField]:
          Object.values(res?.data || {})?.find((item) => Array.isArray(item)) ||
          [],
      }));
    } catch (e) {
      console.log("Error on hierarchy change", e);
    }
  };

  const createRoom = async () => {
    try {
      const payload = {
        mail: mail,

        course_type: selectedCourse,

        filters: selectedValues,

        mode,

        deadline: deadline || null,
        // deadline: deadline || new Date().toISOString(),

        max_attempts: maxAttempts,
      };

      console.log(payload, "create room payload");

      const res = await post(API_CREATE_ROOM, payload);

      console.log(res, "room created successfully");
      const code = res?.data?.room_code;
      setRoomCode(code);
    } catch (e) {
      console.log("Error on creating room", e);
    }
  };

  const resetForm = () => {
    setSelectedCourse("");
    setSelectedValues({});
    setDynamicOptions({});
    setFilterData({});
    setDeadline("");
    setMaxAttempts(1);
    setMode("live");
    setRoomCode("");
  };

  return {
    resetForm,
    getCourseDetails,
    getCourseFilter,
    handleCourseChange,
    course,
    selectedCourse,
    filterData,
    dynamicOptions,
    selectedValues,
    handleHierarchyChange,
    mode,
    setMode,
    createRoom,
    deadline,
    setDeadline,
    maxAttempts,
    setMaxAttempts,
    roomCode,
    setRoomCode,
  };
};
